import React, { useState } from 'react';
import {
  Switch,
  InputNumber,
  Typography,
  Flex,
  ColorPicker,
  Select,
} from 'antd';
import { DownOutlined, UpOutlined } from '@ant-design/icons';
import { FogType, FogPreset } from '@src/components/generated/graphql';
import styles from './graphics-settings.module.css';

const { Option } = Select;

interface FogConfig {
  enabled: boolean;
  type: FogType;
  colorHex: string;
  near: number;
  far: number;
  density: number;
  autoUpdate: boolean;
  preset?: FogPreset;
}

interface FogSettingsProps {
  currentFogConfig: FogConfig;
  callBack: (params: { type: string; value: any; key: string }) => void;
  type: string;
  setDisableBtn: (disabled: boolean) => void;
}

const FOG_PRESETS = {
  [FogPreset.Clear]: {
    enabled: false,
  },
  [FogPreset.LightMist]: {
    enabled: true,
    type: FogType.Exponential,
    colorHex: '#f0f0f0',
    density: 0.0001,
  },
  [FogPreset.MorningFog]: {
    enabled: true,
    type: FogType.Exponential,
    colorHex: '#e6e6fa',
    density: 0.0008,
  },
  [FogPreset.HeavyFog]: {
    enabled: true,
    type: FogType.Exponential,
    colorHex: '#cccccc',
    density: 0.003,
  },
  [FogPreset.DistantHaze]: {
    enabled: true,
    type: FogType.Linear,
    colorHex: '#ddeeff',
    near: 500,
    far: 2000,
  },
  [FogPreset.ThickFog]: {
    enabled: true,
    type: FogType.Exponential,
    colorHex: '#999999',
    density: 0.01,
  },
};

export default function FogSettings({
  currentFogConfig,
  callBack,
  type,
  setDisableBtn,
}: FogSettingsProps) {
  const [isFogEnabled, setIsFogEnabled] = useState(
    currentFogConfig?.enabled || false
  );
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);

  const handleChange = (key: string, value: any) => {
    if (key !== 'preset' && currentFogConfig.preset !== FogPreset.Custom) {
      callBack({ type, value: FogPreset.Custom, key: 'preset' });
    }

    callBack({ type, value, key });
    setDisableBtn(false);
  };

  const handleFogToggle = (enabled: boolean) => {
    setIsFogEnabled(enabled);
    handleChange('enabled', enabled);
  };

  const handlePresetChange = (preset: string) => {
    if (preset === FogPreset.Custom) {
      handleChange('preset', preset);
      return;
    }

    const presetConfig = FOG_PRESETS[preset as keyof typeof FOG_PRESETS];

    if (presetConfig) {
      Object.keys(presetConfig).forEach((key) => {
        if (key === 'enabled') {
          setIsFogEnabled(presetConfig.enabled);
        }
        callBack({
          type,
          value: presetConfig[key as keyof typeof presetConfig],
          key,
        });
      });

      callBack({ type, value: preset, key: 'preset' });
    }
  };

  const toggleAdvancedOptions = () => {
    setShowAdvancedOptions(!showAdvancedOptions);
  };

  if (!currentFogConfig) {
    return null;
  }

  return (
    <>
      <Typography.Title level={5}>Fog Settings</Typography.Title>

      <Flex justify='space-between' align='center' style={{ marginBottom: 16 }}>
        <Typography.Paragraph strong>Enable Fog</Typography.Paragraph>
        <Switch checked={isFogEnabled} onChange={handleFogToggle} />
      </Flex>

      {isFogEnabled && (
        <Flex vertical gap={16}>
          <div>
            <Typography.Paragraph strong>Fog Preset</Typography.Paragraph>
            <Select
              value={currentFogConfig.preset || FogPreset.Custom}
              style={{ width: '100%' }}
              onChange={handlePresetChange}
            >
              <Option value={FogPreset.Clear}>Clear</Option>
              <Option value={FogPreset.LightMist}>Light Mist</Option>
              <Option value={FogPreset.MorningFog}>Morning Fog</Option>
              <Option value={FogPreset.HeavyFog}>Heavy Fog</Option>
              <Option value={FogPreset.DistantHaze}>Distant Haze</Option>
              <Option value={FogPreset.ThickFog}>Thick Fog</Option>
              <Option value={FogPreset.Custom}>Custom</Option>
            </Select>
          </div>

          <div>
            <Typography.Paragraph strong>Fog Type</Typography.Paragraph>
            <Select
              value={currentFogConfig.type}
              style={{ width: '100%' }}
              onChange={(value) => handleChange('type', value)}
            >
              <Option value={FogType.Linear}>Linear</Option>
              <Option value={FogType.Exponential}>Exponential</Option>
            </Select>
          </div>

          <div>
            <Typography.Paragraph strong>Fog Color</Typography.Paragraph>
            <ColorPicker
              showText
              value={currentFogConfig.colorHex}
              onChange={(color) =>
                handleChange('colorHex', color.toHexString())
              }
              style={{ width: '100%' }}
            />
          </div>

          {currentFogConfig.type === FogType.Linear && (
            <>
              <div>
                <Typography.Paragraph strong>
                  Near Distance (1-500)
                </Typography.Paragraph>
                <InputNumber
                  value={currentFogConfig.near}
                  min={1}
                  max={500}
                  style={{ width: '100%' }}
                  onChange={(value) => handleChange('near', value)}
                />
              </div>

              <div>
                <Typography.Paragraph strong>
                  Far Distance (100-5000)
                </Typography.Paragraph>
                <InputNumber
                  value={currentFogConfig.far}
                  min={100}
                  max={5000}
                  style={{ width: '100%' }}
                  onChange={(value) => handleChange('far', value)}
                />
              </div>
            </>
          )}

          {currentFogConfig.type === FogType.Exponential && (
            <div>
              <Typography.Paragraph strong>
                Fog Density (0.0001-0.1)
              </Typography.Paragraph>
              <InputNumber
                value={currentFogConfig.density}
                min={0.0001}
                max={0.1}
                step={0.0001}
                style={{ width: '100%' }}
                onChange={(value) => handleChange('density', value)}
              />
            </div>
          )}

          <div className={styles.optionsToggle} onClick={toggleAdvancedOptions}>
            {showAdvancedOptions ? (
              <>
                <UpOutlined /> Hide Advanced Fog Options
              </>
            ) : (
              <>
                <DownOutlined /> Show Advanced Fog Options
              </>
            )}
          </div>

          {showAdvancedOptions && (
            <Flex vertical gap={12}>
              <Flex justify='space-between' align='center'>
                <Typography.Paragraph strong>Auto Update</Typography.Paragraph>
                <Switch
                  checked={currentFogConfig.autoUpdate}
                  onChange={(value) => handleChange('autoUpdate', value)}
                />
              </Flex>

              {currentFogConfig.type === FogType.Linear && (
                <div>
                  <Typography.Paragraph strong>
                    Fog Density (0.0001-0.1)
                  </Typography.Paragraph>
                  <Typography.Text
                    type='secondary'
                    style={{ fontSize: '12px' }}
                  >
                    This setting is available for reference but primarily used
                    in exponential mode
                  </Typography.Text>
                  <InputNumber
                    value={currentFogConfig.density}
                    min={0.0001}
                    max={0.1}
                    step={0.0001}
                    style={{ width: '100%', marginTop: 8 }}
                    onChange={(value) => handleChange('density', value)}
                  />
                </div>
              )}

              {currentFogConfig.type === FogType.Exponential && (
                <>
                  <div>
                    <Typography.Paragraph strong>
                      Near Distance (1-500)
                    </Typography.Paragraph>
                    <Typography.Text
                      type='secondary'
                      style={{ fontSize: '12px' }}
                    >
                      This setting is available for reference but primarily used
                      in linear mode
                    </Typography.Text>
                    <InputNumber
                      value={currentFogConfig.near}
                      min={1}
                      max={500}
                      style={{ width: '100%', marginTop: 8 }}
                      onChange={(value) => handleChange('near', value)}
                    />
                  </div>

                  <div>
                    <Typography.Paragraph strong>
                      Far Distance (100-5000)
                    </Typography.Paragraph>
                    <Typography.Text
                      type='secondary'
                      style={{ fontSize: '12px' }}
                    >
                      This setting is available for reference but primarily used
                      in linear mode
                    </Typography.Text>
                    <InputNumber
                      value={currentFogConfig.far}
                      min={100}
                      max={5000}
                      style={{ width: '100%', marginTop: 8 }}
                      onChange={(value) => handleChange('far', value)}
                    />
                  </div>
                </>
              )}
            </Flex>
          )}
        </Flex>
      )}
    </>
  );
}
